import { useCallback, useEffect } from 'react';
import useSWR from 'swr';
import { create } from 'zustand';
import { storeService } from 'services';
import { useAuth } from 'hooks';
import { pointOfServiceFactory } from 'factories';
import { PointOfService } from 'types';
import { convertToMilliseconds } from 'helpers';

interface Store {
  storeDetails: PointOfService;
  isLoading: boolean;
  error: unknown | undefined;
  isStoreOpen: boolean;
  isStoreActive: boolean;
}

interface Actions {
  setStoreDetails: (storeDetails: PointOfService) => void;
  setIsLoading: (isLoading: boolean) => void;
  setError: (error: unknown | undefined) => void;
  setIsStoreOpen: (isStoreOpen: boolean) => void;
  setIsStoreActive: (isStoreActive: boolean) => void;
}

const initialState: Store = {
  storeDetails: pointOfServiceFactory(),
  isLoading: true,
  error: undefined,
  isStoreOpen: true,
  isStoreActive: false,
};

const useStore = create<Store & Actions>((set) => ({
  ...initialState,
  setStoreDetails: (storeDetails) => set(() => ({ storeDetails })),
  setIsLoading: (isLoading) => set(() => ({ isLoading })),
  setError: (error) => set(() => ({ error })),
  setIsStoreOpen: (isStoreOpen) => set(() => ({ isStoreOpen })),
  setIsStoreActive: (isStoreActive) => set(() => ({ isStoreActive })),
}));

interface UseGetStoreDetails {
  fullHistory?: boolean;
  revalidate?: boolean;
}

function useGetStoreDetails({
  fullHistory,
  revalidate,
}: UseGetStoreDetails = {}) {
  const { selectedStore, user } = useAuth();
  const { isAdmin } = user;
  const store = selectedStore.name || '';
  const {
    storeDetails,
    isLoading,
    error,
    isStoreOpen,
    isStoreActive,
    setError,
    setIsLoading,
    setStoreDetails,
    setIsStoreOpen,
    setIsStoreActive,
  } = useStore((state) => state);
  const storeDetailsNotLoaded = !storeDetails.baseStore;

  const swrKey = !isAdmin ? `profile-${store}` : null;

  const {
    data,
    isValidating,
    error: swrError,
  } = useSWR(
    swrKey,
    () => storeService.get({ storeNames: store }, fullHistory),
    {
      revalidateOnFocus: false,
      revalidateIfStale: revalidate || storeDetailsNotLoaded,
    }
  );

  const openingHoursTimeout = useCallback(
    (timeout: number, shouldOpen: boolean) => {
      return setTimeout(() => {
        setIsStoreOpen(shouldOpen);
      }, timeout);
    },
    [setIsStoreOpen]
  );

  useEffect(() => {
    if (isValidating) {
      setIsLoading(true);
      setError(undefined);
    }

    if (swrError) {
      setIsLoading(false);
      setError(swrError);
    }

    if (data) {
      setIsLoading(false);
      setError(undefined);
      setStoreDetails(data);

      const currentDate = new Date();
      const weekDay = currentDate
        .toLocaleString('en-us', { weekday: 'long' })
        .toLocaleLowerCase();

      const currentOpeningDay = data.openingDays.find(
        (d) => d.weekday.toLowerCase() === weekDay
      );

      if (currentOpeningDay) {
        const { openingTime, closingTime, open } = currentOpeningDay;

        if (!open) {
          setIsStoreOpen(false);
          return;
        }

        const currentTime = convertToMilliseconds({ date: currentDate });
        const isOpeningHours =
          openingTime <= currentTime && currentTime <= closingTime;

        setIsStoreOpen(isOpeningHours);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isValidating]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    let timeoutCountdown: number;

    const currentDate = new Date();
    const weekDay = currentDate
      .toLocaleString('en-us', { weekday: 'long' })
      .toLocaleLowerCase();

    const currentOpeningDay = storeDetails.openingDays.find(
      (d) => d.weekday.toLowerCase() === weekDay
    );

    if (currentOpeningDay) {
      const { openingTime, closingTime, open } = currentOpeningDay;

      const currentTime = convertToMilliseconds({ date: currentDate });
      const isOpeningHours =
        openingTime <= currentTime && currentTime <= closingTime;

      if (!open) {
        setIsStoreOpen(false);
        return;
      }

      setIsStoreOpen(isOpeningHours);

      if (isOpeningHours) {
        timeoutCountdown = closingTime - currentTime;
        timeout = openingHoursTimeout(timeoutCountdown, false);
      } else if (openingTime > currentTime) {
        timeoutCountdown = openingTime - currentTime;
        timeout = openingHoursTimeout(timeoutCountdown, true);
      }
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [openingHoursTimeout, setIsStoreOpen, storeDetails]);

  useEffect(() => {
    const { participateInPickUpStore, participateInStoreShipping } =
      data ?? storeDetails;

    setIsStoreActive(
      participateInPickUpStore ||
        participateInStoreShipping ||
        storeDetailsNotLoaded
    );
  }, [data, setIsStoreActive, storeDetails, storeDetailsNotLoaded]);

  return {
    storeDetails,
    isLoading,
    error,
    isStoreOpen,
    isStoreActive,
  };
}

export const StoreDetailsStore = {
  useStore,
  useGetStoreDetails,
};
